/**
 * The checks a listing must pass before it can be switched on. Each one maps
 * to an error Google has raised on a Convious listing: a place that is not an
 * admission place, an exclusive category pair, a missing language, a 300 px
 * photo, a listing with no sellable ticket.
 */
import { categoryConflicts } from './categories'

export const MIN_IMAGE_SIZE = 1024

export type ReadinessKey = 'place' | 'categories' | 'description' | 'image' | 'tickets'

export interface ReadinessCheck {
    key: ReadinessKey
    ok: boolean
    /** What is missing, as a value the copy layer can name: a language code, a size, a count. */
    detail?: string
}

export interface ImageSize {
    width: number
    height: number
}

export interface ReadinessInput {
    googlePlaceId: string
    categories: string[]
    descriptions: Array<{ language: string; text: string }>
    /** Languages the account sells in; the description must exist in each. */
    languages: string[]
    image: string
    /** Measured size of the stored image; null while it loads or when it cannot be read. */
    imageSize: ImageSize | null
    /** Tickets on the listing that have a day price today. */
    sellableTickets: number
}

export function readinessChecks(input: ReadinessInput): ReadinessCheck[] {
    const missingLanguages = input.languages.filter(
        (language) => !input.descriptions.find((d) => d.language === language && d.text.trim().length > 0),
    )
    const conflicts = categoryConflicts(input.categories)
    const imageOk =
        !!input.image &&
        !!input.imageSize &&
        input.imageSize.width >= MIN_IMAGE_SIZE &&
        input.imageSize.width === input.imageSize.height

    return [
        { key: 'place', ok: input.googlePlaceId.trim().length > 0 },
        {
            key: 'categories',
            ok: input.categories.length > 0 && conflicts.length === 0,
            detail: conflicts.length ? conflicts[0].join('+') : undefined,
        },
        { key: 'description', ok: missingLanguages.length === 0, detail: missingLanguages.join(', ') || undefined },
        {
            key: 'image',
            ok: imageOk,
            detail: input.imageSize ? `${input.imageSize.width}×${input.imageSize.height}` : undefined,
        },
        { key: 'tickets', ok: input.sellableTickets > 0, detail: String(input.sellableTickets) },
    ]
}

export const isReady = (checks: ReadinessCheck[]): boolean => checks.every((check) => check.ok)
